import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type {
  AIAssistantWorkspaceTabId,
  IntellioChartPanel,
  IntellioVisualization3DSpaceModel,
} from '@triedge/ai-assistant-ui';
import type {
  DomainRouterMutationArgs,
  DomainRouterMutationData,
  DomainRouterQueryArgs,
  DomainRouterQueryData,
} from '../../api/domainRouterClient';
import { domainRouterClient } from '../../api/domainRouterClient';
import { readDomainRouterErrorMessage } from '../../state/domainRouterError';
import {
  clearAssistantChartArtifactStateCacheEntry,
  coerceChartPanels,
  type AssistantChartArtifactResolvedState,
  type AssistantChartArtifactStateResolver,
} from './assistantChartArtifactState';
import {
  clearAssistantVisualization3DArtifactStateCacheEntry,
  coerceVisualization3DSpace,
  type AssistantVisualization3DArtifactResolvedState,
  type AssistantVisualization3DArtifactStateResolver,
} from './assistantVisualization3DArtifactState';
import { buildAssistantBridgeStatus } from './assistantSpreadsheetBridgeStatus';
import {
  ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE,
  buildAssistantSpreadsheetBridgeCallbackUri,
  clearAssistantSpreadsheetBridgeReturnContext,
  consumeAssistantSpreadsheetBridgeCompletionContext,
  storeAssistantSpreadsheetBridgeReturnContext,
} from './assistantSpreadsheetBridgeClientState';
import { toArtifactWorkspaceRef } from './assistantArtifactWorkspaceProjector';
import type {
  AdmissionsAssistantArtifactWorkspaceRef,
  AdmissionsAssistantSpreadsheetViewContext,
} from './types';

export type ActiveSpreadsheetWorkspace = {
  workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
  sourceTaskId?: string | null;
  sourceUpdatedAt?: number | null;
  view?: AdmissionsAssistantSpreadsheetViewContext | null;
};

export type ActiveChartWorkspace = {
  chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  chartPanels?: IntellioChartPanel[];
};

export type ActiveVisualization3DWorkspace = {
  visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  visualization3DSpace?: IntellioVisualization3DSpaceModel | null;
  visualization3DPanels?: IntellioChartPanel[];
};

type ReportWorkspaceTab = 'templates' | 'filters' | 'history';

type ChartArtifactStateRequestCacheEntry = {
  inFlight?: Promise<AssistantChartArtifactResolvedState | null>;
  resolved?: AssistantChartArtifactResolvedState | null;
};

type Visualization3DArtifactStateRequestCacheEntry = {
  inFlight?: Promise<AssistantVisualization3DArtifactResolvedState | null>;
  resolved?: AssistantVisualization3DArtifactResolvedState | null;
};

type ReportWorkspaceController = {
  isOpen: boolean;
  closeWorkspace: () => void;
  openWorkspace: (tab?: ReportWorkspaceTab) => void;
};

type QueryTrigger<Name extends string, Args, Data> = (
  operation: Name,
  args: Args,
  preferCacheValue?: boolean,
) => Promise<Data>;

type MutationTrigger<Args, Data> = (args: Args) => Promise<Data>;

type AssistantWorkspaceTaskLaneControllerInput = {
  pathname: string;
  search: string;
  panelState: {
    isOverlay: boolean;
    isExpanded: boolean;
    setExpanded: (expanded: boolean) => void;
  };
  reportWorkspace: ReportWorkspaceController;
  setAssistantSurfaceView: Dispatch<SetStateAction<AIAssistantWorkspaceTabId>>;
  triggerGetAssistantSpreadsheetWorkbookDraftState: QueryTrigger<
    'getAssistantSpreadsheetWorkbookDraftState',
    DomainRouterQueryArgs<'getAssistantSpreadsheetWorkbookDraftState'>,
    DomainRouterQueryData<'getAssistantSpreadsheetWorkbookDraftState'>
  >;
  triggerGetAssistantSpreadsheetBridgeStatus: QueryTrigger<
    'getAssistantSpreadsheetBridgeStatus',
    DomainRouterQueryArgs<'getAssistantSpreadsheetBridgeStatus'>,
    DomainRouterQueryData<'getAssistantSpreadsheetBridgeStatus'>
  >;
  triggerStartAssistantSpreadsheetBridgeAuth: MutationTrigger<
    DomainRouterMutationArgs<'startAssistantSpreadsheetBridgeAuth'>,
    DomainRouterMutationData<'startAssistantSpreadsheetBridgeAuth'>
  >;
  triggerExportAssistantSpreadsheetToGoogleDrive: MutationTrigger<
    DomainRouterMutationArgs<'exportAssistantSpreadsheetToGoogleDrive'>,
    DomainRouterMutationData<'exportAssistantSpreadsheetToGoogleDrive'>
  >;
  triggerDisconnectAssistantSpreadsheetBridge: MutationTrigger<
    DomainRouterMutationArgs<'disconnectAssistantSpreadsheetBridge'>,
    DomainRouterMutationData<'disconnectAssistantSpreadsheetBridge'>
  >;
  triggerSaveAssistantSpreadsheetExportPreset: MutationTrigger<
    DomainRouterMutationArgs<'saveAssistantSpreadsheetExportPreset'>,
    DomainRouterMutationData<'saveAssistantSpreadsheetExportPreset'>
  >;
  triggerDeleteAssistantSpreadsheetExportPreset: MutationTrigger<
    DomainRouterMutationArgs<'deleteAssistantSpreadsheetExportPreset'>,
    DomainRouterMutationData<'deleteAssistantSpreadsheetExportPreset'>
  >;
};

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

export const buildSpreadsheetViewContextFromSession = (
  session: ActiveSpreadsheetWorkspace | null | undefined,
): AdmissionsAssistantSpreadsheetViewContext | undefined => {
  if (!session?.workbookArtifact.sourceRef) {
    return undefined;
  }
  return {
    workbookArtifactPath: session.workbookArtifact.sourceRef ?? null,
    workbookTitle: session.workbookArtifact.label ?? null,
    activeSheetId: session.view?.activeSheetId ?? null,
    activeSheetName: session.view?.activeSheetName ?? null,
    visibleRowCount: session.view?.visibleRowCount ?? null,
    totalRowCount: session.view?.totalRowCount ?? null,
    activeFilters: session.view?.activeFilters ?? [],
  };
};

export const useAssistantWorkspaceTaskLaneController = (
  input: AssistantWorkspaceTaskLaneControllerInput,
) => {
  const {
    pathname,
    search,
    panelState,
    reportWorkspace,
    setAssistantSurfaceView,
    triggerGetAssistantSpreadsheetWorkbookDraftState,
    triggerGetAssistantSpreadsheetBridgeStatus,
    triggerStartAssistantSpreadsheetBridgeAuth,
    triggerExportAssistantSpreadsheetToGoogleDrive,
    triggerDisconnectAssistantSpreadsheetBridge,
    triggerSaveAssistantSpreadsheetExportPreset,
    triggerDeleteAssistantSpreadsheetExportPreset,
  } = input;
  const reportWorkspaceOpen = reportWorkspace.isOpen;
  const openReportWorkspaceLane = reportWorkspace.openWorkspace;
  const closeReportWorkspaceLane = reportWorkspace.closeWorkspace;
  const [activeSpreadsheetWorkspace, setActiveSpreadsheetWorkspace] = useState<ActiveSpreadsheetWorkspace | null>(null);
  const [currentSpreadsheetSession, setCurrentSpreadsheetSession] = useState<ActiveSpreadsheetWorkspace | null>(null);
  const [activeChartWorkspace, setActiveChartWorkspace] = useState<ActiveChartWorkspace | null>(null);
  const [activeVisualization3DWorkspace, setActiveVisualization3DWorkspace] = useState<ActiveVisualization3DWorkspace | null>(null);
  const chartArtifactStateRequestCacheRef = useRef<Map<string, ChartArtifactStateRequestCacheEntry>>(new Map());
  const visualization3DArtifactStateRequestCacheRef = useRef<Map<string, Visualization3DArtifactStateRequestCacheEntry>>(new Map());
  const [spreadsheetBridgeConnected, setSpreadsheetBridgeConnected] = useState(false);
  const [spreadsheetBridgeStatusLabel, setSpreadsheetBridgeStatusLabel] = useState('unknown');
  const [spreadsheetBridgeState, setSpreadsheetBridgeState] = useState<'connected' | 'not_connected' | 'download_only' | 'needs_attention'>('not_connected');
  const [spreadsheetBridgeSupportedEnvironment, setSpreadsheetBridgeSupportedEnvironment] = useState(true);
  const [spreadsheetBridgePresets, setSpreadsheetBridgePresets] = useState<Array<Record<string, unknown>>>([]);
  const [spreadsheetExportState, setSpreadsheetExportState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [spreadsheetExportMessage, setSpreadsheetExportMessage] = useState<string | null>(null);

  const closeChartWorkspace = useCallback(() => {
    setAssistantSurfaceView('chat');
    setActiveChartWorkspace((current) => {
      const cacheKey = String(current?.chartArtifact.sourceRef ?? '').trim();
      if (cacheKey) {
        chartArtifactStateRequestCacheRef.current.delete(cacheKey);
      }
      clearAssistantChartArtifactStateCacheEntry(current?.chartArtifact ?? null);
      return null;
    });
  }, [setAssistantSurfaceView]);

  const closeVisualization3DWorkspace = useCallback(() => {
    setAssistantSurfaceView('chat');
    setActiveVisualization3DWorkspace((current) => {
      const cacheKey = String(current?.visualization3DArtifact.sourceRef ?? '').trim();
      if (cacheKey) {
        visualization3DArtifactStateRequestCacheRef.current.delete(cacheKey);
      }
      clearAssistantVisualization3DArtifactStateCacheEntry(current?.visualization3DArtifact ?? null);
      return null;
    });
  }, [setAssistantSurfaceView]);

  const closeSpreadsheetWorkspace = useCallback(() => {
    setAssistantSurfaceView('chat');
    setActiveSpreadsheetWorkspace(null);
  }, [setAssistantSurfaceView]);

  const closeReportWorkspace = useCallback(() => {
    closeReportWorkspaceLane();
  }, [closeReportWorkspaceLane]);

  const storeSpreadsheetSession = useCallback((sessionInput: {
    workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
    spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
    sourceTaskId?: string | null;
    sourceUpdatedAt?: number | null;
    view?: AdmissionsAssistantSpreadsheetViewContext | null;
  }) => {
    const nextSession = {
      workbookArtifact: sessionInput.workbookArtifact,
      spreadsheetDownloadArtifact: sessionInput.spreadsheetDownloadArtifact ?? null,
      sourceTaskId: sessionInput.sourceTaskId ?? null,
      sourceUpdatedAt: sessionInput.sourceUpdatedAt ?? null,
      view: sessionInput.view ?? null,
    };
    setCurrentSpreadsheetSession(nextSession);
    setSpreadsheetExportState('idle');
    setSpreadsheetExportMessage(null);
    return nextSession;
  }, []);

  const handleSpreadsheetWorkspaceSessionChange = useCallback((session: ActiveSpreadsheetWorkspace) => {
    setCurrentSpreadsheetSession(session);
    setActiveSpreadsheetWorkspace((current) => (current ? session : current));
  }, []);

  const refreshSpreadsheetWorkspaceSession = useCallback(async (sessionInput: {
    workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
    spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
    sourceTaskId?: string | null;
    sourceUpdatedAt?: number | null;
    view?: AdmissionsAssistantSpreadsheetViewContext | null;
  }) => {
    if (!sessionInput.workbookArtifact.sourceRef) {
      return;
    }
    try {
      const payload = await triggerGetAssistantSpreadsheetWorkbookDraftState(
        'getAssistantSpreadsheetWorkbookDraftState',
        { workbookArtifactPath: sessionInput.workbookArtifact.sourceRef },
      );
      const record = toRecord(payload);
      const responsePayload = toRecord(record?.payload);
      const nextWorkbookArtifact = toArtifactWorkspaceRef(responsePayload?.workbookArtifact);
      const nextDownloadArtifact = toArtifactWorkspaceRef(responsePayload?.spreadsheetDownloadArtifact);
      if (!nextWorkbookArtifact) {
        return;
      }
      const nextSession = storeSpreadsheetSession({
        workbookArtifact: nextWorkbookArtifact,
        spreadsheetDownloadArtifact: nextDownloadArtifact ?? null,
        sourceTaskId: sessionInput.sourceTaskId ?? null,
        sourceUpdatedAt: sessionInput.sourceUpdatedAt ?? null,
        view: sessionInput.view ?? null,
      });
      handleSpreadsheetWorkspaceSessionChange(nextSession);
    } catch (error) {
      console.warn('[AdmissionsAI] Failed to refresh spreadsheet workspace session', error);
    }
  }, [
    handleSpreadsheetWorkspaceSessionChange,
    storeSpreadsheetSession,
    triggerGetAssistantSpreadsheetWorkbookDraftState,
  ]);

  const openSpreadsheetWorkspace = useCallback((sessionInput: {
    workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
    spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
    sourceTaskId?: string | null;
    sourceUpdatedAt?: number | null;
    view?: AdmissionsAssistantSpreadsheetViewContext | null;
	  }) => {
	    closeReportWorkspaceLane();
	    closeChartWorkspace();
	    closeVisualization3DWorkspace();
	    setAssistantSurfaceView('chat');
    const nextSession = storeSpreadsheetSession(sessionInput);
    setActiveSpreadsheetWorkspace(nextSession);
    void refreshSpreadsheetWorkspaceSession(sessionInput);
	  }, [
	    closeChartWorkspace,
	    closeVisualization3DWorkspace,
	    closeReportWorkspaceLane,
    refreshSpreadsheetWorkspaceSession,
    setAssistantSurfaceView,
    storeSpreadsheetSession,
  ]);

	  const openReportWorkspace = useCallback((tab: ReportWorkspaceTab = 'templates') => {
	    setActiveSpreadsheetWorkspace(null);
	    closeChartWorkspace();
	    closeVisualization3DWorkspace();
	    openReportWorkspaceLane(tab);
    if (panelState.isOverlay && !panelState.isExpanded) {
      panelState.setExpanded(true);
    }
    setAssistantSurfaceView('chat');
  }, [
	    closeChartWorkspace,
	    closeVisualization3DWorkspace,
	    panelState,
    openReportWorkspaceLane,
    setAssistantSurfaceView,
  ]);

  const openChartWorkspace = useCallback((workspaceInput: {
    chartArtifact: AdmissionsAssistantArtifactWorkspaceRef;
    chartPanels?: IntellioChartPanel[];
	  }) => {
	    closeReportWorkspaceLane();
	    setActiveSpreadsheetWorkspace(null);
	    closeVisualization3DWorkspace();
	    setAssistantSurfaceView('chat');
    setActiveChartWorkspace({
      chartArtifact: workspaceInput.chartArtifact,
      chartPanels: workspaceInput.chartPanels ?? [],
    });
	  }, [closeReportWorkspaceLane, closeVisualization3DWorkspace, setAssistantSurfaceView]);

	  const refreshChartArtifactState = useCallback<AssistantChartArtifactStateResolver>(async (chartArtifact) => {
    const cacheKey = String(chartArtifact.sourceRef ?? '').trim();
    if (!cacheKey) {
      return null;
    }

    const existingEntry = chartArtifactStateRequestCacheRef.current.get(cacheKey);
    if (existingEntry && Object.prototype.hasOwnProperty.call(existingEntry, 'resolved')) {
      return existingEntry.resolved ?? null;
    }
    if (existingEntry?.inFlight) {
      return existingEntry.inFlight;
    }

    const inFlight = domainRouterClient.query(
      'getAssistantChartArtifactState',
      {
        sourceRef: cacheKey,
        label: chartArtifact.label,
        fileName: chartArtifact.fileName,
        mimeType: chartArtifact.mimeType,
        route: pathname,
      },
    )
      .then((payload) => {
        const record = toRecord(payload);
        const refreshedArtifact = toArtifactWorkspaceRef(record?.chartArtifact);
        const resolved = refreshedArtifact ? {
          chartArtifact: refreshedArtifact,
          chartPanels: coerceChartPanels(record?.chartPanels),
        } satisfies AssistantChartArtifactResolvedState : null;
        chartArtifactStateRequestCacheRef.current.set(cacheKey, { resolved });
        return resolved;
      })
      .catch((error) => {
        const latestEntry = chartArtifactStateRequestCacheRef.current.get(cacheKey);
        if (latestEntry?.resolved) {
          return latestEntry.resolved;
        }
        chartArtifactStateRequestCacheRef.current.delete(cacheKey);
        throw error;
      });

    chartArtifactStateRequestCacheRef.current.set(cacheKey, { inFlight });
    return inFlight;
	  }, [pathname]);

	  const openVisualization3DWorkspace = useCallback((workspaceInput: {
	    visualization3DArtifact: AdmissionsAssistantArtifactWorkspaceRef;
	    visualization3DSpace?: IntellioVisualization3DSpaceModel | null;
	    visualization3DPanels?: IntellioChartPanel[];
	  }) => {
	    closeReportWorkspaceLane();
	    setActiveSpreadsheetWorkspace(null);
	    closeChartWorkspace();
	    setAssistantSurfaceView('chat');
	    setActiveVisualization3DWorkspace({
	      visualization3DArtifact: workspaceInput.visualization3DArtifact,
	      visualization3DSpace: workspaceInput.visualization3DSpace ?? null,
	      visualization3DPanels: workspaceInput.visualization3DPanels ?? [],
	    });
	  }, [closeChartWorkspace, closeReportWorkspaceLane, setAssistantSurfaceView]);

	  const refreshVisualization3DArtifactState = useCallback<AssistantVisualization3DArtifactStateResolver>(async (visualization3DArtifact) => {
	    const cacheKey = String(visualization3DArtifact.sourceRef ?? '').trim();
	    if (!cacheKey) {
	      return null;
	    }

	    const existingEntry = visualization3DArtifactStateRequestCacheRef.current.get(cacheKey);
	    if (existingEntry && Object.prototype.hasOwnProperty.call(existingEntry, 'resolved')) {
	      return existingEntry.resolved ?? null;
	    }
	    if (existingEntry?.inFlight) {
	      return existingEntry.inFlight;
	    }

	    const inFlight = domainRouterClient.query(
	      'getAssistantVisualization3DArtifactState',
	      {
	        sourceRef: cacheKey,
	        label: visualization3DArtifact.label,
	        fileName: visualization3DArtifact.fileName,
	        mimeType: visualization3DArtifact.mimeType,
	        route: pathname,
	      },
	    )
	      .then((payload) => {
	        const record = toRecord(payload);
	        const refreshedArtifact = toArtifactWorkspaceRef(record?.visualization3DArtifact);
	        const space = coerceVisualization3DSpace(record?.visualization3DSpace);
	        const resolved = refreshedArtifact && space ? {
	          visualization3DArtifact: refreshedArtifact,
	          visualization3DSpace: space,
	          visualization3DPanels: coerceChartPanels(record?.visualization3DPanels),
	        } satisfies AssistantVisualization3DArtifactResolvedState : null;
	        visualization3DArtifactStateRequestCacheRef.current.set(cacheKey, { resolved });
	        return resolved;
	      })
	      .catch((error) => {
	        const latestEntry = visualization3DArtifactStateRequestCacheRef.current.get(cacheKey);
	        if (latestEntry?.resolved) {
	          return latestEntry.resolved;
	        }
	        visualization3DArtifactStateRequestCacheRef.current.delete(cacheKey);
	        throw error;
	      });

	    visualization3DArtifactStateRequestCacheRef.current.set(cacheKey, { inFlight });
	    return inFlight;
	  }, [pathname]);

  const refreshSpreadsheetBridgeStatus = useCallback(async () => {
    try {
      const payload = await triggerGetAssistantSpreadsheetBridgeStatus(
        'getAssistantSpreadsheetBridgeStatus',
        {},
      );
      const status = buildAssistantBridgeStatus(payload);
      setSpreadsheetBridgeConnected(status.connected);
      setSpreadsheetBridgeStatusLabel(status.statusLabel);
      setSpreadsheetBridgeState(status.bridgeState);
      setSpreadsheetBridgeSupportedEnvironment(status.supportedEnvironment);
      setSpreadsheetBridgePresets(status.presets);
      return status;
    } catch (error) {
      console.warn('[AdmissionsAI] Failed to load spreadsheet bridge status', error);
      setSpreadsheetBridgeConnected(false);
      setSpreadsheetBridgeStatusLabel('unavailable');
      setSpreadsheetBridgeState('needs_attention');
      setSpreadsheetBridgeSupportedEnvironment(false);
      setSpreadsheetBridgePresets([]);
      return {
        connected: false,
        statusLabel: 'unavailable',
        bridgeState: 'needs_attention' as const,
        supportedEnvironment: false,
        presets: [],
        targetRoute: ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE,
      };
    }
  }, [triggerGetAssistantSpreadsheetBridgeStatus]);

  useEffect(() => {
    if (!activeSpreadsheetWorkspace) {
      return;
    }
    void refreshSpreadsheetBridgeStatus();
  }, [activeSpreadsheetWorkspace, refreshSpreadsheetBridgeStatus]);

  useEffect(() => {
    const currentRoute = `${pathname}${search}`;
    const completionContext = consumeAssistantSpreadsheetBridgeCompletionContext();
    if (!completionContext || completionContext.originRoute !== currentRoute) {
      return;
    }

    const workbookArtifactPath = completionContext.workbookArtifactPath ?? '';
    const restore = async () => {
      try {
        if (workbookArtifactPath) {
          const payload = await triggerGetAssistantSpreadsheetWorkbookDraftState(
            'getAssistantSpreadsheetWorkbookDraftState',
            { workbookArtifactPath },
          );
          const record = toRecord(payload);
          const responsePayload = toRecord(record?.payload);
          const nextWorkbookArtifact = toArtifactWorkspaceRef(responsePayload?.workbookArtifact);
          const nextDownloadArtifact = toArtifactWorkspaceRef(responsePayload?.spreadsheetDownloadArtifact);
          if (nextWorkbookArtifact) {
            storeSpreadsheetSession({
              workbookArtifact: nextWorkbookArtifact,
              spreadsheetDownloadArtifact: nextDownloadArtifact ?? null,
            });
          }
        }
        await refreshSpreadsheetBridgeStatus();
      } catch (error) {
        console.warn('[AdmissionsAI] Failed to restore spreadsheet bridge completion context', error);
      } finally {
        clearAssistantSpreadsheetBridgeReturnContext();
      }
    };

    void restore();
  }, [
    pathname,
    search,
    refreshSpreadsheetBridgeStatus,
    storeSpreadsheetSession,
    triggerGetAssistantSpreadsheetWorkbookDraftState,
  ]);

  const exportSpreadsheetWorkspace = useCallback(async (destination?: {
    fileName?: string;
    destinationFolderId?: string;
    destinationFileId?: string;
  }) => {
    if (!activeSpreadsheetWorkspace?.workbookArtifact.sourceRef) {
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage('The workbook artifact path is unavailable for Google Sheets export.');
      return;
    }

    setSpreadsheetExportState('submitting');
    setSpreadsheetExportMessage('Exporting workbook to Google Sheets...');
    try {
      const result = await triggerExportAssistantSpreadsheetToGoogleDrive({
        input: {
          fileName: destination?.fileName?.replace(/\.xlsx$/i, '')
            ?? activeSpreadsheetWorkspace.spreadsheetDownloadArtifact?.fileName.replace(/\.xlsx$/i, '')
            ?? activeSpreadsheetWorkspace.workbookArtifact.fileName.replace(/\.json$/i, ''),
          workbookArtifactPath: activeSpreadsheetWorkspace.workbookArtifact.sourceRef,
          destinationFolderId: destination?.destinationFolderId,
          destinationFileId: destination?.destinationFileId,
        },
      });
      const record = toRecord(result);
      const ok = Boolean(record?.ok);
      const responseData = toRecord(record?.data);
      const fileUrl = typeof responseData?.fileUrl === 'string'
        ? responseData.fileUrl
        : typeof record?.fileUrl === 'string'
          ? record.fileUrl
          : null;
      const message = typeof record?.message === 'string'
        ? record.message
        : ok
          ? 'Spreadsheet exported to Google Sheets.'
          : 'Spreadsheet export failed.';

      setSpreadsheetExportState(ok ? 'success' : 'error');
      setSpreadsheetExportMessage(ok && fileUrl ? `${message} Open: ${fileUrl}` : message);
      await refreshSpreadsheetBridgeStatus();
    } catch (error) {
      console.error('[AdmissionsAI] Failed to export assistant spreadsheet', error);
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage('Unable to export the workbook to Google Sheets right now.');
    }
  }, [
    activeSpreadsheetWorkspace,
    refreshSpreadsheetBridgeStatus,
    triggerExportAssistantSpreadsheetToGoogleDrive,
  ]);

  const startSpreadsheetBridgeAuth = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const redirectUri = buildAssistantSpreadsheetBridgeCallbackUri({
      returnTo: `${pathname}${search}` || ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE,
    });
    try {
      const result = await triggerStartAssistantSpreadsheetBridgeAuth({
        input: {
          redirectUri,
        },
      });
      const record = toRecord(result);
      const payload = toRecord(record?.payload);
      const authorizationUrl = typeof record?.authorizationUrl === 'string'
        ? record.authorizationUrl
        : typeof payload?.authorizationUrl === 'string'
          ? payload.authorizationUrl
          : null;
      const message = typeof record?.message === 'string'
        ? record.message
        : 'Unable to start spreadsheet bridge auth.';

      if (!authorizationUrl) {
        setSpreadsheetExportState('error');
        setSpreadsheetExportMessage(message);
        return;
      }

      const currentSession = activeSpreadsheetWorkspace ?? currentSpreadsheetSession;
      storeAssistantSpreadsheetBridgeReturnContext({
        originRoute: `${pathname}${search}`,
        workbookArtifactPath: currentSession?.workbookArtifact.sourceRef ?? null,
        workbookLabel: currentSession?.workbookArtifact.label ?? null,
        workbookFileName: currentSession?.spreadsheetDownloadArtifact?.fileName ?? currentSession?.workbookArtifact.fileName ?? null,
        spreadsheetDownloadPath: currentSession?.spreadsheetDownloadArtifact?.sourceRef ?? null,
        startedAt: new Date().toISOString(),
      });
      window.location.assign(authorizationUrl);
    } catch (error) {
      console.error('[AdmissionsAI] Failed to start spreadsheet bridge auth', error);
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage(readDomainRouterErrorMessage(error, 'Unable to start spreadsheet bridge auth right now.'));
    }
  }, [
    activeSpreadsheetWorkspace,
    currentSpreadsheetSession,
    pathname,
    search,
    triggerStartAssistantSpreadsheetBridgeAuth,
  ]);

  const disconnectSpreadsheetBridge = useCallback(async () => {
    try {
      await triggerDisconnectAssistantSpreadsheetBridge({
        input: {},
      });
      await refreshSpreadsheetBridgeStatus();
      setSpreadsheetExportMessage('Google Sheets export bridge disconnected.');
      setSpreadsheetExportState('success');
    } catch (error) {
      console.error('[AdmissionsAI] Failed to disconnect spreadsheet bridge', error);
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage(readDomainRouterErrorMessage(error, 'Unable to disconnect the spreadsheet bridge right now.'));
    }
  }, [refreshSpreadsheetBridgeStatus, triggerDisconnectAssistantSpreadsheetBridge]);

  const saveSpreadsheetExportPreset = useCallback(async (presetInput: {
    presetId?: string;
    label: string;
    destinationType: 'new_sheet' | 'existing_sheet';
    destinationFileId?: string;
    destinationFolderId?: string;
    spreadsheetUrl?: string | null;
    fileNameTemplate?: string | null;
  }) => {
    try {
      await triggerSaveAssistantSpreadsheetExportPreset({
        input: presetInput,
      });
      await refreshSpreadsheetBridgeStatus();
    } catch (error) {
      console.error('[AdmissionsAI] Failed to save spreadsheet export preset', error);
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage(readDomainRouterErrorMessage(error, 'Unable to save the spreadsheet export preset.'));
    }
  }, [refreshSpreadsheetBridgeStatus, triggerSaveAssistantSpreadsheetExportPreset]);

  const deleteSpreadsheetExportPreset = useCallback(async (presetInput: { presetId: string }) => {
    try {
      await triggerDeleteAssistantSpreadsheetExportPreset({
        input: presetInput,
      });
      await refreshSpreadsheetBridgeStatus();
    } catch (error) {
      console.error('[AdmissionsAI] Failed to delete spreadsheet export preset', error);
      setSpreadsheetExportState('error');
      setSpreadsheetExportMessage(readDomainRouterErrorMessage(error, 'Unable to delete the spreadsheet export preset.'));
    }
  }, [refreshSpreadsheetBridgeStatus, triggerDeleteAssistantSpreadsheetExportPreset]);

  const closeActiveWorkspace = useCallback(() => {
    if (reportWorkspaceOpen) {
      closeReportWorkspaceLane();
      return;
    }
    if (activeSpreadsheetWorkspace) {
      closeSpreadsheetWorkspace();
      return;
    }
	    if (activeChartWorkspace) {
	      closeChartWorkspace();
	      return;
	    }
	    if (activeVisualization3DWorkspace) {
	      closeVisualization3DWorkspace();
	    }
	  }, [
	    closeSpreadsheetWorkspace,
	    closeChartWorkspace,
	    closeVisualization3DWorkspace,
	    activeChartWorkspace,
	    activeVisualization3DWorkspace,
	    activeSpreadsheetWorkspace,
    closeReportWorkspaceLane,
    reportWorkspaceOpen,
  ]);

  const resetWorkspaceTaskLaneState = useCallback(() => {
	    setActiveSpreadsheetWorkspace(null);
	    setCurrentSpreadsheetSession(null);
	    closeChartWorkspace();
	    closeVisualization3DWorkspace();
	    setSpreadsheetExportState('idle');
    setSpreadsheetExportMessage(null);
	  }, [closeChartWorkspace, closeVisualization3DWorkspace]);

	  const activeWorkspaceKind = useMemo<'report-builder' | 'spreadsheet' | 'chart' | 'visualization3d' | null>(() => {
	    if (reportWorkspaceOpen) {
	      return 'report-builder';
	    }
    if (activeSpreadsheetWorkspace) {
      return 'spreadsheet';
    }
	    if (activeChartWorkspace) {
	      return 'chart';
	    }
	    if (activeVisualization3DWorkspace) {
	      return 'visualization3d';
	    }
	    return null;
	  }, [
	    activeChartWorkspace,
	    activeVisualization3DWorkspace,
	    activeSpreadsheetWorkspace,
	    reportWorkspaceOpen,
	  ]);

  return {
    activeSpreadsheetWorkspace,
	    currentSpreadsheetSession,
	    activeChartWorkspace,
	    activeVisualization3DWorkspace,
    spreadsheetBridgeConnected,
    spreadsheetBridgeStatusLabel,
    spreadsheetBridgeState,
    spreadsheetBridgeSupportedEnvironment,
    spreadsheetBridgePresets,
    spreadsheetExportState,
    spreadsheetExportMessage,
    activeWorkspaceKind,
    storeSpreadsheetSession,
    handleSpreadsheetWorkspaceSessionChange,
    openSpreadsheetWorkspace,
    closeSpreadsheetWorkspace,
    openReportWorkspace,
    closeReportWorkspace,
	    openChartWorkspace,
	    closeChartWorkspace,
	    openVisualization3DWorkspace,
	    closeVisualization3DWorkspace,
	    closeActiveWorkspace,
	    refreshChartArtifactState,
	    refreshVisualization3DArtifactState,
    refreshSpreadsheetBridgeStatus,
    exportSpreadsheetWorkspace,
    startSpreadsheetBridgeAuth,
    disconnectSpreadsheetBridge,
    saveSpreadsheetExportPreset,
    deleteSpreadsheetExportPreset,
    resetWorkspaceTaskLaneState,
  };
};
